import React from "react";
import {Text, Stack, Box} from "@nike/podium";
import {Tip} from "@nike/nike-design-system-icons";
import {BucketData, BucketFilters, ActionedRecord, BUCKET_CONFIGS} from "../../common/data/types";
import {SummaryCards} from "./SummaryCards";
import {Bucket} from "./Bucket";

type CleanupsAdjustAllProps = {
    bucketData: BucketData;
    bucketFilters: BucketFilters;
    onBucketFilterChange: (key: 'zero' | 'under' | 'over', val: string) => void;
    onRemoveRecord: (key: 'zero' | 'under' | 'over', idx: number) => void;
    onPerformAction: (key: 'zero' | 'under' | 'over') => void;
    actionedCodes: Record<string, ActionedRecord>;
};

export function CleanupsAdjustAll({
    bucketData, bucketFilters, onBucketFilterChange,
    onRemoveRecord, onPerformAction, actionedCodes
}: CleanupsAdjustAllProps) {
    const hasData = bucketData.zero.length + bucketData.under.length + bucketData.over.length > 0;

    if (!hasData) {
        return (
            <Stack align="center" justify="center" gap="s" className="im-ca-empty">
                <Tip size="l" />
                <Text font="body-2" color="secondary" weight="medium">No suggestions yet</Text>
                <Text font="body-3" color="secondary" style={{textAlign: 'center', maxWidth: 340}}>
                    Go to <strong>Historic Imbalances</strong>, set your persistence threshold,
                    then click <strong>Suggest adjustments</strong>.
                </Text>
            </Stack>
        );
    }

    const isDone = (key: 'zero' | 'under' | 'over') => {
        const rows = bucketData[key];
        return rows.length > 0 && rows.every(item => actionedCodes[item.material]?.status === 'processed');
    };

    return (
        <Stack style={{flex: 1, overflow: 'hidden'}}>
            <SummaryCards bucketData={bucketData} actionedCodes={actionedCodes} />
            <Box className="im-ca-buckets" style={{flex: 1, overflowY: 'auto'}}>
                <Stack gap="s">
                    {BUCKET_CONFIGS.map((config, idx) => (
                        <Bucket
                            key={config.key}
                            config={config}
                            rows={bucketData[config.key]}
                            filter={bucketFilters[config.key]}
                            onFilterChange={(val) => onBucketFilterChange(config.key, val)}
                            onRemoveRecord={(origIdx) => onRemoveRecord(config.key, origIdx)}
                            onPerformAction={() => onPerformAction(config.key)}
                            isDone={isDone(config.key)}
                            startOpen={idx === 0}
                        />
                    ))}
                </Stack>
            </Box>
        </Stack>
    );
}
